"use client";
import CustomNavLink from "@/components/CustomLink/CustomNavLink";
import WishListLoader from "@/components/Loading/WishListLoader";
import Image from "next/image";
import React, { useState } from "react";
import NotifyButton from "./NotifyButton/NotifyButton";
import { useDictionary } from "../DictProvider/DictProvider";
import { Button } from "@mui/material";
import { AddShoppingCart } from "@mui/icons-material";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import useCart from "@/hooks/useCart";
import { usePathname, useRouter } from "next/navigation";
import useImageUrlModifier from "@/lib/useImageUrlModifier";
import VideoPlayer from "@/app/[lang]/(withHeader)/category/_components/ProductSlider/VideoPlayer";
import isImage from "@/lib/isImage";
import { isUnder21 } from "@/lib/isUnder21";
import { useLoggedIn } from "@/store/useAuthStore";
import AgeConsent from "../AgeConsent/AgeConsent";

const Product = ({ product }: { product: any }) => {

  const { auth, profile } = useLoggedIn();
  const { addtCartList, cart } = useCart();
  const translate = useDictionary();
  const Router = useRouter();
  const pathname = usePathname();

  const isCarted = (productId: any) => {
    return cart?.items?.some((item: any) => item?.product?.id === productId);
  };
  const fallbackImage = "/images/dummy-image-square.jpg"; // Fallback image

  const modifyImageUrl = useImageUrlModifier();

  const [modalOpen, setModalOpen] = useState(false);
  const [whoRun, setWhoRun] = useState<(() => void) | null>(null);

  const handleOpenModal = (runMainFunc: any) => {
    if (!auth) {
      setModalOpen(true);
      setWhoRun(() => runMainFunc);
      return;
    }

    if (!profile?.DOB) {
      setModalOpen(true);
      setWhoRun(() => runMainFunc);
      return;
    }

    if (isUnder21(profile.DOB)) {
      setWhoRun(() => runMainFunc);
      setModalOpen(true);
      return;
    }

    // ✅ Only when logged in, DOB exists, and age >= 21
    runMainFunc();
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setWhoRun(null);
  };

  const handleConfirm = () => {
    setModalOpen(false);
    if (whoRun) {
      whoRun();
      setWhoRun(null);
    }
  };

  const uniqueProducts = product?.filter(
    (item: any, index: number, arr: any[]) =>
      arr.findIndex((p: any) => p?.id === item?.id) === index
  );

  return (
    <>
      {uniqueProducts?.map((item: any) => (
        <div
          key={item?.id}
          className="relative flex flex-col justify-between shadow-[0px_2px_3px_-1px_rgba(0,0,0,0.1),0px_1px_0px_0px_rgba(25,28,33,0.02),0px_0px_0px_1px_rgba(25,28,33,0.08)] bg-white rounded-lg overflow-hidden transition-transform duration-300"
        >
          <div className="m-3">
            <div className="w-full relative !aspect-square">
              <CustomNavLink href={`product/${item?.language?.productSlug}`}>
                <div className="!aspect-square">
                  {isImage(item?.pictures[0]) ? (
                    <Image
                      src={
                        item?.pictures?.length > 0
                          ? modifyImageUrl(item?.pictures[0], 300, 300) ||
                            fallbackImage
                          : fallbackImage
                      }
                      alt={item?.language?.product}
                      className="w-full aspect-square rounded-lg object-contain group-hover:scale-105 transition-all duration-300"
                      layout="responsive"
                      width={1}
                      height={1}
                      quality={100} // Set to 100 for the best quality
                      style={{ borderRadius: "6px" }}
                      placeholder="blur"
                      blurDataURL={fallbackImage} // Low-quality version for blur
                      priority={true}
                      sizes="auto" // Ensure sizes are correctly defined for responsive layouts
                      unoptimized
                    />
                  ) : (
                    <VideoPlayer
                      videoUrl={item?.pictures[0]}
                      play={true}
                      stock={item?.stock === 0 ? true : false}
                      productId={item?.id}
                    />
                  )}
                </div>
              </CustomNavLink>
              <WishListLoader isCategory={true} id={item?.id} />
              {/* BOGO STICKER */}
              {item?.stock > 0
                ? !!item?.bogo &&
                  !!item?.sticker && (
                    <div className="absolute bottom-0 right-0">
                      <Image
                        layout="responsive"
                        width={102}
                        height={36}
                        src={item?.sticker?.replace(
                          /^https:\/\/res\.cloudinary\.com/,
                          "https://res.cloudinary.com"
                        )}
                        alt="bogo"
                        className="object-contain max-w-[102px]"
                      />
                    </div>
                  )
                : item?.composition?.trim() === "Simple product" && (
                    <div className="absolute bottom-0 right-0 z-10 xl:hidden">
                      <NotifyButton productId={item?.id} />
                    </div>
                  )}
            </div>
            <div>
              <div className="flex gap-x-1 my-2">
                <Image
                  src={"/images/star.svg"}
                  width={20}
                  height={20}
                  alt="star"
                  className="hidden lg:block"
                />
                <Image
                  src={"/images/star.svg"}
                  width={16}
                  height={16}
                  alt="star"
                  className="lg:hidden"
                />
                <p className="  text-[13px] md:text-base xl:text-lg font-semibold">
                  {item?.rating > 0 ? item?.rating : 4.5}
                </p>
              </div>
              <p
                className="text-[13px] md:text-base xl:text-lg pb-[8px]"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  lineHeight: "1.2", // Adjust this for better spacing
                  height: "calc(1.2em * 2)", // Restricts the height to 2 lines
                }}
              >
                {item?.language?.product}
              </p>

              <div className="w-full h-fit flex justify-between items-end">
                <div className="w-fit flex justify-between items-end">
                  <div>
                    {/* STRIKED PRICE AND PERCENTAGE OFFER */}
                    {!item?.bogo &&
                    (!!item?.strikedPrice || !!item?.percentage) ? (
                      <div className={`flex gap-1 items-center justify-start`}>
                        {!item?.percentage && !!item?.strikedPrice && (
                          <p className="text-gray-700 pb-[6px] text-xs line-through text-nowrap">
                            {item?.strikedPrice?.toFixed(3)} KWD
                          </p>
                        )}

                        {!!item?.percentage && !!item?.strikedPrice && (
                          <>
                            <p className="text-gray-700 text-xs line-through text-nowrap">
                              {item?.strikedPrice?.toFixed(3)} KWD
                            </p>
                            <span className="w-fit bg-green-100 text-nowrap text-green-800 text-xs font-semibold px-[6px] py-[3px] rounded">
                              {item?.percentage}% OFF
                            </span>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="h-[22px] lg:h-[24px]"></div>
                    )}
                    <p className="text-lg font-bold text-black text-nowrap">
                      {parseFloat(item?.sellingPrice).toFixed(3)} KWD
                    </p>
                  </div>
                </div>
                {/* DESKTOP ADD BUTTON */}
                {item?.stock > 0 &&
                  item?.composition?.trim() === "Product with Variants" && (
                    <CustomNavLink
                      href={`product/${item?.language?.productSlug}`}
                      className="hidden xl:block"
                    >
                      <Button
                        variant="contained"
                        startIcon={
                          <AddShoppingCart
                            sx={{
                              margin:
                                pathname?.split("/")[1] === "en"
                                  ? "0 0 4px 0"
                                  : "0 0 0 4px",
                            }}
                          />
                        }
                        className="w-fit text-nowrap py-2 mt-2 transition-all duration-300 ease-in-out transform"
                        sx={{
                          padding:
                            pathname?.split("/")[1] === "en"
                              ? "8px 12px"
                              : "8px 0px 8px 4px",
                          borderRadius: "6px",
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                          fontWeight: "500",
                          fontSize: "12px",
                          margin: 0,
                          textTransform: "none",
                          backgroundColor: "#AC7529",
                          "&:hover": {
                            backgroundColor: "#8E6022",
                          },
                        }}
                      >
                        {translate.poductDescriptionPage.add}
                      </Button>
                    </CustomNavLink>
                  )}
                {/* DESKTOP ADD TO CART BUTTON */}
                {item?.stock > 0 &&
                  item?.composition?.trim() === "Simple product" && (
                    <div className="hidden xl:block">
                      {" "}
                      <Button
                        onClick={() => {
                          if (!item?.id) return;

                          if (isCarted(item.id)) {
                            Router.push(`/${pathname.split("/")[1]}/cart`);
                            return;
                          }

                          const meta = {
                            price: item?.sellingPrice ? Number(item.sellingPrice) : undefined,
                            name: item?.language?.product,
                            brand: item?.language?.brand,
                            category: item?.categoryMappings?.[0]?.category,
                            barcode: item?.barcode,
                          };
                          if (item?.restricted) {
                            // ✅ Restricted product → defer `addtCartList` until age/auth checks pass
                            handleOpenModal(() => addtCartList(item.id, 1, meta));
                          } else {
                            // ✅ Unrestricted → add directly
                            addtCartList(item.id, 1, meta);
                          }
                        }}
                        variant="contained"
                        startIcon={
                          <AddShoppingCart
                            sx={{
                              margin:
                                pathname?.split("/")[1] === "en"
                                  ? "0 0 4px 0"
                                  : "0 0 0 4px",
                            }}
                          />
                        }
                        className="w-fit text-nowrap py-2 mt-2 transition-all duration-300 ease-in-out transform"
                        sx={{
                          padding:
                            pathname?.split("/")[1] === "en"
                              ? "8px 12px"
                              : "8px 0px 8px 4px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          margin: 0,
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                          fontWeight: "500",
                          textTransform: "none",
                          backgroundColor: "#AC7529",
                          "&:hover": {
                            backgroundColor: "#8E6022",
                          },
                        }}
                      >
                        {isCarted(item?.id)
                          ? translate.poductDescriptionPage.goToCart
                          : translate.poductDescriptionPage.addtocart}
                      </Button>
                    </div>
                  )}
              </div>
            </div>
          </div>
          {/* MOBILE ADD BUTTON */}
          {item?.stock > 0 &&
            item?.composition?.trim() === "Product with Variants" && (
              <CustomNavLink
                href={`product/${item?.language?.productSlug}`}
                className="xl:hidden"
              >
                <Button
                  variant="contained"
                  startIcon={<AddShoppingCart sx={{ mx: 1 }} />}
                  className="w-full py-2 mt-2 transition-all duration-300 ease-in-out transform"
                  sx={{
                    borderRadius: "0 0 6px 6px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    fontWeight: "500",
                    textTransform: "none",
                    backgroundColor: "#AC7529",
                    "&:hover": {
                      backgroundColor: "#8E6022",
                    },
                  }}
                >
                  {translate.poductDescriptionPage.add}
                </Button>
              </CustomNavLink>
            )}
          {/* MOBILE ADD TO CART BUTTON */}
          {item?.stock > 0 &&
            item?.composition?.trim() === "Simple product" && (
              <div className="xl:hidden">
                {" "}
                <Button
                  onClick={() => {
                    if (!item?.id) return;

                    if (isCarted(item.id)) {
                      Router.push(`/${pathname.split("/")[1]}/cart`);
                      return;
                    }

                    const meta = {
                      price: item?.sellingPrice ? Number(item.sellingPrice) : undefined,
                      name: item?.language?.product,
                      brand: item?.language?.brand,
                      category: item?.categoryMappings?.[0]?.category,
                      barcode: item?.barcode,
                    };
                    if (item?.restricted) {
                      // ✅ Restricted product → defer `addtCartList` until age/auth checks pass
                      handleOpenModal(() => addtCartList(item.id, 1, meta));
                    } else {
                      // ✅ Unrestricted → add directly
                      addtCartList(item.id, 1, meta);
                    }
                  }}
                  variant="contained"
                  startIcon={<AddShoppingCart sx={{ mx: 1 }} />}
                  className="w-full py-2 mt-2 transition-all duration-300 ease-in-out transform"
                  sx={{
                    borderRadius: "0 0 6px 6px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    fontWeight: "500",
                    textTransform: "none",
                    backgroundColor: "#AC7529",
                    "&:hover": {
                      backgroundColor: "#8E6022",
                    },
                  }}
                >
                  {isCarted(item?.id)
                    ? translate.poductDescriptionPage.goToCart
                    : translate.poductDescriptionPage.addtocart}
                </Button>
              </div>
            )}

          {/* OUT OF STOCK OVERLAY */}
          {item?.stock === 0 && (
              <div
                onClick={() => {
                  Router.push(
                    `/${pathname.split("/")[1]}/product/${
                      item?.language?.productSlug
                    }`
                  );
                }}
                className="w-full cursor-pointer h-full absolute inset-0 bg-[#E0E0E0]/50 rounded-md p-4"
              >
                <div className="relative inset-0 flex flex-col justify-between items-center text-white font-semibold w-full h-full aspect-square text-center">
                  <p className="rounded-b-[6px] text-sm bg-[#555555] py-1 px-4">
                    {translate.poductDescriptionPage.outOfStock}
                  </p>
                  <div className="w-full xl:flex justify-end hidden">
                    <NotifyButton productId={item?.id} />
                  </div>
                </div>
              </div>
            )}
          {/* MOBILE VIEW PRODUCT IF OUT OF STACK */}
          {item?.stock === 0 && (
            <CustomNavLink
              href={`product/${item?.language?.productSlug}`}
              className="xl:hidden"
            >
              <Button
                variant="contained"
                startIcon={<ViewCarouselIcon sx={{ mx: 1 }} />}
                className="w-full py-2 mt-2 transition-all duration-300 ease-in-out transform"
                sx={{
                  borderRadius: "0 0 6px 6px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  fontWeight: "500",
                  textTransform: "none",
                  backgroundColor: "#AC7529",
                  "&:hover": {
                    backgroundColor: "#8E6022",
                  },
                }}
              >
                {translate.poductDescriptionPage.viewProducts}
              </Button>
            </CustomNavLink>
          )}
        </div>
      ))}

      <AgeConsent
        open={modalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default Product;
